import { useEffect, useMemo, useState, useRef } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import BrandLogo from "@/components/BrandLogo";
import { Download, Printer, Plus, Minus, Globe, Hash, IdCard } from "lucide-react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function downloadSvg(svgEl, filename) {
  const svg = svgEl.outerHTML;
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function PosterCard({ label, sublabel, url, accent = "brand", footnote = "Scan to order at the canteen" }) {
  const ref = useRef(null);
  const accentBg = accent === "sun" ? "bg-sun" : accent === "ink" ? "bg-ink text-white" : "bg-brand text-white";
  return (
    <div className="bg-white rounded-3xl border border-border overflow-hidden shadow-soft" data-testid={`qr-poster-${label}`}>
      <div className={`p-6 ${accentBg}`}>
        <BrandLogo size="md" className={accent === "sun" ? "" : "text-white"} />
        <div className="mt-2 text-xs font-bold uppercase tracking-[0.2em] opacity-80">{footnote}</div>
      </div>
      <div className="p-8 flex flex-col items-center text-center bg-cream-100">
        <div ref={ref} className="bg-white rounded-2xl p-4 shadow-soft">
          <QRCodeSVG value={url} size={220} level="M" includeMargin={false} fgColor="#1C1917" />
        </div>
        <div className="mt-4 font-heading text-3xl font-extrabold text-ink">{label}</div>
        {sublabel && <div className="text-sm text-ink-500 mt-1">{sublabel}</div>}
      </div>
      <div className="p-4 border-t border-border flex items-center justify-between gap-2">
        <span className="text-[11px] font-mono text-ink-500 truncate">{url.replace(/^https?:\/\//, "")}</span>
        <div className="flex gap-1">
          <button data-testid={`download-${label}`} onClick={() => downloadSvg(ref.current.querySelector("svg"), `grubpe-${label}.svg`)}
            className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300" title="Download SVG">
            <Download size={14} />
          </button>
          <button onClick={() => window.print()} className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300" title="Print">
            <Printer size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminQRCodes() {
  const [tab, setTab] = useState("universal");
  const [tables, setTables] = useState(8);
  const [students, setStudents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");

  useEffect(() => {
    api.get("/admin/users?role=student").then(r => setStudents(r.data.filter(s => s.rfid_card_id)));
    api.get("/vendors").then(r => setVendors(r.data));
  }, []);

  const universalUrl = useMemo(() => {
    const u = new URL(`${ORIGIN}/qr`);
    if (selectedVendor) u.searchParams.set("vendor", selectedVendor);
    return u.toString();
  }, [selectedVendor]);

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">QR Ordering</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">QR codes & posters</h1>
            <p className="text-sm text-ink-600 mt-2 max-w-2xl">Print these and stick them on tables, walls, or hand them to students. Each scan opens the Grubpe ordering page directly on the diner's phone.</p>
          </div>
          <div className="inline-flex bg-cream-200 rounded-full p-1">
            {[
              { k: "universal", label: "Universal", icon: Globe },
              { k: "tables", label: "Per-table", icon: Hash },
              { k: "students", label: "Student cards", icon: IdCard },
            ].map(t => {
              const Icon = t.icon;
              return (
                <button key={t.k} data-testid={`qr-tab-${t.k}`} onClick={() => setTab(t.k)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${tab === t.k ? "bg-ink text-white" : "text-ink-700"}`}>
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "universal" && (
          <div>
            <div className="bg-white rounded-2xl border border-border p-5 mb-5">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Optional: filter to one vendor brand</label>
              <select data-testid="qr-vendor-filter" value={selectedVendor} onChange={e => setSelectedVendor(e.target.value)}
                className="mt-2 w-full sm:w-80 rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none">
                <option value="">All vendors (full menu)</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <PosterCard label="Order here" sublabel={selectedVendor ? vendors.find(v=>v.id===selectedVendor)?.name : "All canteen menus"} url={universalUrl} accent="brand" />
            </div>
          </div>
        )}

        {tab === "tables" && (
          <div>
            <div className="bg-white rounded-2xl border border-border p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Number of tables</label>
                <div className="mt-1 flex items-center gap-3">
                  <button data-testid="tables-dec" onClick={() => setTables(Math.max(1, tables - 1))} className="w-9 h-9 rounded-full bg-cream-200 hover:bg-cream-300 flex items-center justify-center"><Minus size={14} /></button>
                  <span className="font-heading text-2xl font-extrabold w-10 text-center" data-testid="tables-count">{tables}</span>
                  <button data-testid="tables-inc" onClick={() => setTables(Math.min(50, tables + 1))} className="w-9 h-9 rounded-full bg-ink text-white hover:bg-ink-700 flex items-center justify-center"><Plus size={14} /></button>
                </div>
              </div>
              <div className="text-xs text-ink-500 max-w-md">Each QR carries <span className="font-mono">?table=T1</span> so the order shows the table number for canteen runners.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: tables }).map((_, i) => {
                const num = `T${i + 1}`;
                const url = `${ORIGIN}/qr?table=${num}`;
                return <PosterCard key={num} label={`Table ${num}`} sublabel="Order to this table" url={url} accent={i % 2 ? "ink" : "brand"} />;
              })}
            </div>
          </div>
        )}

        {tab === "students" && (
          <div>
            <div className="bg-white rounded-2xl border border-border p-5 mb-5">
              <div className="text-sm text-ink-600">Student-personal QR cards. Each card embeds the student's <span className="font-mono">?rfid=RFID-XXX</span>. When scanned at a kiosk, the order auto-charges that student's wallet — no login required.</div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">⚠️ Prototype: anyone with the QR could order. Production version should embed a signed token.</div>
            </div>
            {students.length === 0 ? (
              <div className="text-sm text-ink-500 bg-cream-100 p-6 rounded-2xl border border-border">No students with RFID cards yet. Assign cards from <span className="font-bold">Users & RFID</span>.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {students.map(s => (
                  <PosterCard
                    key={s.id}
                    label={s.name}
                    sublabel={`${s.grade || ""} · ${s.rfid_card_id}`}
                    url={`${ORIGIN}/qr?rfid=${encodeURIComponent(s.rfid_card_id)}`}
                    accent="sun"
                    footnote="Personal Grubpe card"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
